import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { MoreHorizontal, Plus, Trash2, UserMinus } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Modal, Drawer, PlanLockedCard, useToast, useConfirm, ErrorState } from '@/components/ui';
import {
  sequencesApi,
  Sequence,
  SequenceStep,
  SequenceEnrollment,
  KeigoRegister,
} from '@/lib/sequencesApi';
import { contactsApi, Contact } from '@/lib/contactsApi';
import { usePlan } from '@/hooks/usePlan';
import { formatLocaleDate } from '@/lib/date';

const REGISTER_KEYS: Record<KeigoRegister, string> = {
  first_formal: 'sequences.register_first_formal',
  follow_up: 'sequences.register_follow_up',
  introduced: 'sequences.register_introduced',
  warm: 'sequences.register_warm',
};

const ENROLL_STATUS_KEYS: Record<SequenceEnrollment['status'], string> = {
  active: 'sequences.enroll_status_active',
  paused: 'sequences.enroll_status_paused',
  finished: 'sequences.enroll_status_finished',
  replied: 'sequences.enroll_status_replied',
  bounced: 'sequences.enroll_status_bounced',
};

const ENROLL_STATUS_COLOR: Record<SequenceEnrollment['status'], string> = {
  active: 'bg-emerald-50 text-emerald-700',
  paused: 'bg-amber-50 text-amber-700',
  finished: 'bg-ink-100 text-ink-600',
  replied: 'bg-accent-50 text-accent-700',
  bounced: 'bg-warn-50 text-warn-600',
};

function EnrollmentsSection({ sequence, wsId }: { sequence: Sequence; wsId: string }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();

  const enrollmentsQ = useQuery({
    queryKey: ['sequence-enrollments', sequence.id],
    queryFn: () => sequencesApi.enrollments(sequence.id),
  });

  // Resolve contact ids → display names. One bounded fetch; unknown ids
  // fall back to a short id so the row is never blank.
  const contactsQ = useQuery({
    queryKey: ['contacts', wsId, 'enroll-names'],
    queryFn: () => contactsApi.list({ workspaceId: wsId, limit: 200 }),
    enabled: !!wsId,
  });
  const nameById = new Map(
    (contactsQ.data?.items ?? []).map((c: Contact) => [c.id, c.displayName]),
  );

  const unenroll = useMutation({
    mutationFn: (enrollmentId: string) => sequencesApi.unenroll(sequence.id, enrollmentId),
    onSuccess: () => {
      toast.success(t('sequences.unenrolled'));
      qc.invalidateQueries({ queryKey: ['sequence-enrollments', sequence.id] });
    },
    onError: () => toast.error(t('common.delete_failed')),
  });

  const askUnenroll = async (e: SequenceEnrollment) => {
    const ok = await confirm({
      title: t('sequences.unenroll_confirm_title'),
      description: t('sequences.unenroll_confirm_body'),
      confirmLabel: t('sequences.unenroll'),
      variant: 'danger',
    });
    if (ok) unenroll.mutate(e.id);
  };

  const rows = enrollmentsQ.data ?? [];

  return (
    <section>
      <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
        {t('sequences.drawer_enrollments')}
      </h3>
      {enrollmentsQ.isLoading && (
        <p className="text-xs text-ink-500">{t('common.loading')}</p>
      )}
      {!enrollmentsQ.isLoading && rows.length === 0 && (
        <p className="text-xs text-ink-500">{t('sequences.no_enrollments')}</p>
      )}
      {rows.length > 0 && (
        <ul className="divide-y divide-ink-100 rounded-md border border-ink-200">
          {rows.map((e) => (
            <li key={e.id} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-ink-900">
                  {nameById.get(e.contactId) ?? `#${e.contactId.slice(0, 8)}`}
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-ink-500">
                  <span>
                    {t('sequences.col_step')}{' '}
                    <span className="num">{e.currentStep + 1}</span>
                  </span>
                  {e.nextActionAt && (
                    <>
                      <span className="text-ink-300">·</span>
                      <span className="num">
                        {formatLocaleDate(e.nextActionAt, i18n.language, { withTime: true })}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <span
                className={`shrink-0 text-[10px] uppercase tracking-wide font-semibold rounded-md px-1.5 py-0.5 ${ENROLL_STATUS_COLOR[e.status]}`}
              >
                {t(ENROLL_STATUS_KEYS[e.status])}
              </span>
              <button
                type="button"
                onClick={() => askUnenroll(e)}
                disabled={unenroll.isPending}
                aria-label={t('sequences.unenroll')}
                title={t('sequences.unenroll')}
                className="shrink-0 rounded p-1 text-warn-600 hover:bg-warn-500/10 disabled:opacity-50"
              >
                <UserMinus className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const STEP_KIND_KEYS: Record<SequenceStep['kind'], string> = {
  email: 'sequences.step_kind_email',
  task: 'sequences.step_kind_task',
  wait: 'sequences.step_kind_wait',
};

interface NewSeqForm {
  name: string;
  language: 'ja' | 'en';
  keigoRegister: KeigoRegister;
}

const EMPTY_NEW: NewSeqForm = {
  name: '',
  language: 'ja',
  keigoRegister: 'first_formal',
};

function SequenceCreateForm({
  initial,
  onCancel,
  onSubmit,
  submitting,
}: {
  initial: NewSeqForm;
  onCancel: () => void;
  onSubmit: (values: NewSeqForm) => void;
  submitting: boolean;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<NewSeqForm>(initial);

  const set = <K extends keyof NewSeqForm>(k: K, v: NewSeqForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const canSubmit = form.name.trim().length > 0 && !submitting;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit(form);
      }}
      className="space-y-4"
    >
      <label className="block">
        <span className="text-xs text-ink-600">
          {t('sequences.form_name')} <span className="text-warn-600">*</span>
        </span>
        <input
          type="text"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          required
          className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        />
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs text-ink-600">{t('sequences.form_language')}</span>
          <select
            value={form.language}
            onChange={(e) => set('language', e.target.value as 'ja' | 'en')}
            className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          >
            <option value="ja">日本語</option>
            <option value="en">English</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-ink-600">{t('sequences.form_register')}</span>
          <select
            value={form.keigoRegister}
            onChange={(e) => set('keigoRegister', e.target.value as KeigoRegister)}
            className="mt-1 w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          >
            {(Object.keys(REGISTER_KEYS) as KeigoRegister[]).map((r) => (
              <option key={r} value={r}>
                {t(REGISTER_KEYS[r])}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-sm px-3 py-1.5 rounded-md text-ink-700 hover:bg-ink-100"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="text-sm px-3 py-1.5 rounded-md bg-accent-500 text-white font-medium hover:bg-accent-600 disabled:opacity-60"
        >
          {submitting ? t('common.creating') : t('sequences.create_button')}
        </button>
      </div>
    </form>
  );
}

function SequenceDrawerBody({
  wsId,
  sequence,
  onUpdated,
}: {
  wsId: string;
  sequence: Sequence;
  onUpdated: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [steps, setSteps] = useState<SequenceStep[]>(() => [...sequence.steps]);

  const update = useMutation({
    mutationFn: () => sequencesApi.update(sequence.id, { steps }),
    onSuccess: () => {
      toast.success(t('sequences.updated'));
      onUpdated();
    },
    onError: () => toast.error(t('common.save_failed')),
  });

  const addStep = () =>
    setSteps((s) => [...s, { kind: 'email', delayDays: 0, subject: '', body: '' }]);
  const removeStep = (i: number) =>
    setSteps((s) => s.filter((_, idx) => idx !== i));
  const setStep = <K extends keyof SequenceStep>(
    i: number,
    k: K,
    v: SequenceStep[K],
  ) => setSteps((s) => s.map((st, idx) => (idx === i ? { ...st, [k]: v } : st)));

  return (
    <div className="space-y-6 text-sm">
      <section>
        <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
          {t('sequences.drawer_overview')}
        </h3>
        <dl className="space-y-1.5">
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">{t('sequences.col_language')}</dt>
            <dd className="text-ink-900 num">{sequence.language}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">{t('sequences.col_register')}</dt>
            <dd className="text-ink-900">
              {sequence.keigoRegister
                ? t(REGISTER_KEYS[sequence.keigoRegister])
                : '—'}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-ink-500">{t('sequences.col_active')}</dt>
            <dd className="text-ink-900">
              {sequence.active ? t('common.yes') : t('common.no')}
            </dd>
          </div>
        </dl>
      </section>

      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs uppercase tracking-wide text-ink-500">
            {t('sequences.drawer_steps')}
          </h3>
          <button
            type="button"
            onClick={addStep}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100"
          >
            <Plus className="h-3 w-3" />
            {t('sequences.add_step')}
          </button>
        </div>

        {steps.length === 0 && (
          <p className="text-xs text-ink-500">
            {t('sequences.drawer_empty_steps')}
          </p>
        )}

        <ul className="space-y-3">
          {steps.map((s, i) => (
            <li
              key={i}
              className="rounded-md border border-ink-200 p-3 space-y-2"
            >
              <div className="flex items-start gap-2">
                <span className="text-xs num text-ink-500 mt-1">{i + 1}.</span>
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="text-[11px] text-ink-500">
                      {t('sequences.step_kind')}
                    </span>
                    <select
                      value={s.kind}
                      onChange={(e) =>
                        setStep(i, 'kind', e.target.value as SequenceStep['kind'])
                      }
                      className="mt-0.5 w-full rounded-md border border-ink-300 px-2 py-1 text-xs"
                    >
                      {(Object.keys(STEP_KIND_KEYS) as SequenceStep['kind'][]).map(
                        (k) => (
                          <option key={k} value={k}>
                            {t(STEP_KIND_KEYS[k])}
                          </option>
                        ),
                      )}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-[11px] text-ink-500">
                      {t('sequences.step_delay_days')}
                    </span>
                    <input
                      type="number"
                      min={0}
                      value={s.delayDays ?? 0}
                      onChange={(e) =>
                        setStep(i, 'delayDays', Number(e.target.value))
                      }
                      className="mt-0.5 w-full rounded-md border border-ink-300 px-2 py-1 text-xs num"
                    />
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => removeStep(i)}
                  aria-label={t('sequences.remove_step')}
                  className="shrink-0 rounded p-1 text-warn-600 hover:bg-warn-500/10"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              {s.kind === 'email' && (
                <>
                  <label className="block">
                    <span className="text-[11px] text-ink-500">
                      {t('sequences.step_subject')}
                    </span>
                    <input
                      type="text"
                      value={s.subject ?? ''}
                      onChange={(e) => setStep(i, 'subject', e.target.value)}
                      className="mt-0.5 w-full rounded-md border border-ink-300 px-2 py-1 text-xs"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] text-ink-500">
                      {t('sequences.step_body')}
                    </span>
                    <textarea
                      value={s.body ?? ''}
                      onChange={(e) => setStep(i, 'body', e.target.value)}
                      rows={3}
                      className="mt-0.5 w-full rounded-md border border-ink-300 px-2 py-1 text-xs"
                    />
                  </label>
                </>
              )}
              {s.kind === 'task' && (
                <label className="block">
                  <span className="text-[11px] text-ink-500">
                    {t('sequences.step_task_title')}
                  </span>
                  <input
                    type="text"
                    value={s.taskTitle ?? ''}
                    onChange={(e) => setStep(i, 'taskTitle', e.target.value)}
                    className="mt-0.5 w-full rounded-md border border-ink-300 px-2 py-1 text-xs"
                  />
                </label>
              )}
            </li>
          ))}
        </ul>

        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={() => update.mutate()}
            disabled={update.isPending}
            className="text-xs px-3 py-1.5 rounded-md bg-accent-500 text-white font-medium hover:bg-accent-600 disabled:opacity-60"
          >
            {update.isPending
              ? t('common.saving')
              : t('sequences.save_steps')}
          </button>
        </div>
      </section>

      <EnrollmentsSection sequence={sequence} wsId={wsId} />
    </div>
  );
}

function EnrollContactsBody({
  wsId,
  sequence,
  onDone,
}: {
  wsId: string;
  sequence: Sequence;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const list = useQuery({
    queryKey: ['contacts', wsId, search, 'enroll-picker'],
    queryFn: () =>
      contactsApi.list({
        workspaceId: wsId,
        search: search || undefined,
        limit: 50,
      }),
    enabled: !!wsId,
  });

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const enroll = useMutation({
    mutationFn: async () => {
      const ids = Array.from(selected);
      const results = await Promise.allSettled(
        ids.map((id) => sequencesApi.enroll(sequence.id, id)),
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      return { ok, total: ids.length };
    },
    onSuccess: ({ ok, total }) => {
      if (ok === total) {
        toast.success(t('sequences.enroll_success', { count: ok }));
      } else {
        toast.error(t('sequences.enroll_partial', { ok, total }));
      }
      onDone();
    },
    onError: () => toast.error(t('sequences.enroll_failed')),
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-600">{t('sequences.enroll_intro')}</p>
      <input
        type="search"
        placeholder={t('sequences.enroll_searching')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
      />
      <div className="max-h-72 overflow-y-auto rounded-md border border-ink-200 divide-y divide-ink-100">
        {list.isLoading && (
          <p className="px-3 py-2 text-xs text-ink-500">{t('common.loading')}</p>
        )}
        {(list.data?.items ?? []).map((c: Contact) => (
          <label
            key={c.id}
            className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-ink-50 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={selected.has(c.id)}
              onChange={() => toggle(c.id)}
            />
            <div className="min-w-0 flex-1">
              <div className="font-medium text-ink-900 truncate">
                {c.displayName}
              </div>
              {c.email && (
                <div className="text-[11px] text-ink-500 num truncate">
                  {c.email}
                </div>
              )}
            </div>
          </label>
        ))}
      </div>
      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onDone}
          className="text-sm px-3 py-1.5 rounded-md text-ink-700 hover:bg-ink-100"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={() => enroll.mutate()}
          disabled={selected.size === 0 || enroll.isPending}
          className="text-sm px-3 py-1.5 rounded-md bg-accent-500 text-white font-medium hover:bg-accent-600 disabled:opacity-60"
        >
          {enroll.isPending ? t('common.saving') : t('sequences.enroll_submit')}
        </button>
      </div>
    </div>
  );
}

export function SequencesPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const plan = usePlan();

  const [createOpen, setCreateOpen] = useState(false);
  const [drawerSeq, setDrawerSeq] = useState<Sequence | null>(null);
  const [enrollSeq, setEnrollSeq] = useState<Sequence | null>(null);

  // Sequences require Growth — show an upgrade card instead of loading data.
  // Wait for the plan to resolve to avoid flashing the lock on slow networks.
  const planAllows = plan.meets('growth');

  const list = useQuery({
    queryKey: ['sequences', wsId],
    queryFn: () => sequencesApi.list(wsId),
    enabled: !!wsId && planAllows,
  });

  const create = useMutation({
    mutationFn: (v: NewSeqForm) =>
      sequencesApi.create({
        workspaceId: wsId,
        name: v.name,
        language: v.language,
        keigoRegister: v.keigoRegister,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences', wsId] });
      setCreateOpen(false);
      toast.success(t('sequences.created'));
    },
    onError: () => toast.error(t('sequences.create_failed')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => sequencesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sequences', wsId] });
      toast.success(t('sequences.deleted'));
    },
    onError: () => toast.error(t('common.delete_failed')),
  });

  const askDelete = async (s: Sequence) => {
    const ok = await confirm({
      title: t('sequences.confirm_delete_title'),
      description: t('sequences.confirm_delete_body', { name: s.name }),
      confirmLabel: t('common.delete'),
      variant: 'danger',
    });
    if (ok) remove.mutate(s.id);
  };

  const items: Sequence[] = list.data ?? [];

  if (!plan.isLoading && !planAllows) {
    return (
      <AppShell titleKey="sequences.title">
        <PlanLockedCard
          required="growth"
          description={t('plan.locked_sequences_body')}
        />
      </AppShell>
    );
  }

  return (
    <AppShell titleKey="sequences.title">
      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-ink-600 max-w-2xl">{t('sequences.intro')}</p>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="text-xs px-3 py-1.5 rounded-md bg-accent-500 text-white hover:bg-accent-600"
        >
          {t('sequences.new_button')}
        </button>
      </div>

      {list.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}
      {list.isError && <ErrorState onRetry={() => list.refetch()} />}
      {!list.isLoading && !list.isError && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-ink-300 p-10 text-center text-ink-500 max-w-3xl">
          <p className="text-sm">{t('sequences.empty_title')}</p>
          <p className="text-xs mt-2">{t('sequences.empty_sub')}</p>
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm bg-white rounded-2xl border border-ink-200">
            <thead className="text-left text-ink-500 border-b border-ink-200">
              <tr>
                <th className="py-2.5 px-3 font-medium">{t('sequences.col_name')}</th>
                <th className="py-2.5 px-3 font-medium">{t('sequences.col_language')}</th>
                <th className="py-2.5 px-3 font-medium">{t('sequences.col_register')}</th>
                <th className="py-2.5 px-3 font-medium">{t('sequences.col_steps')}</th>
                <th className="py-2.5 px-3 font-medium">{t('sequences.col_active')}</th>
                <th className="py-2.5 px-3 font-medium w-10" aria-label={t('common.actions')} />
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr
                  key={s.id}
                  className="border-b border-ink-100 hover:bg-ink-50 cursor-pointer"
                  onClick={() => setDrawerSeq(s)}
                >
                  <td className="py-2.5 px-3 font-medium text-ink-900">{s.name}</td>
                  <td className="py-2.5 px-3 num text-ink-700">{s.language}</td>
                  <td className="py-2.5 px-3 text-ink-700">
                    {s.keigoRegister ? t(REGISTER_KEYS[s.keigoRegister]) : '—'}
                  </td>
                  <td className="py-2.5 px-3 num text-ink-700">{s.steps.length}</td>
                  <td className="py-2.5 px-3 num text-ink-700">{s.active ? '✓' : '—'}</td>
                  <td
                    className="py-2.5 px-3 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger asChild>
                        <button
                          type="button"
                          aria-label={t('sequences.row_actions')}
                          className="rounded-md p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.Content
                          align="end"
                          sideOffset={4}
                          className="min-w-[160px] rounded-md border border-ink-200 bg-white p-1 text-sm"
                        >
                          <DropdownMenu.Item
                            onSelect={() => setEnrollSeq(s)}
                            className="cursor-pointer rounded-sm px-2 py-1 text-ink-700 outline-none data-[highlighted]:bg-ink-100"
                          >
                            {t('sequences.enroll_button')}
                          </DropdownMenu.Item>
                          <DropdownMenu.Item
                            onSelect={() => askDelete(s)}
                            className="cursor-pointer rounded-sm px-2 py-1 text-warn-600 outline-none data-[highlighted]:bg-warn-50"
                          >
                            {t('common.delete')}
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-6 text-xs text-ink-500 max-w-3xl">
        {t('sequences.scheduler_note')}
      </p>

      <Modal
        open={createOpen}
        onOpenChange={setCreateOpen}
        title={t('sequences.new_title')}
        size="md"
      >
        <SequenceCreateForm
          initial={EMPTY_NEW}
          onCancel={() => setCreateOpen(false)}
          onSubmit={(v) => create.mutate(v)}
          submitting={create.isPending}
        />
      </Modal>

      <Drawer
        open={!!drawerSeq}
        onOpenChange={(o) => !o && setDrawerSeq(null)}
        title={drawerSeq?.name ?? ''}
      >
        {drawerSeq && (
          <SequenceDrawerBody
            wsId={wsId}
            sequence={drawerSeq}
            onUpdated={() => {
              qc.invalidateQueries({ queryKey: ['sequences', wsId] });
            }}
          />
        )}
      </Drawer>

      <Modal
        open={!!enrollSeq}
        onOpenChange={(o) => !o && setEnrollSeq(null)}
        title={
          enrollSeq
            ? t('sequences.enroll_title', { name: enrollSeq.name })
            : ''
        }
        size="md"
      >
        {enrollSeq && (
          <EnrollContactsBody
            wsId={wsId}
            sequence={enrollSeq}
            onDone={() => setEnrollSeq(null)}
          />
        )}
      </Modal>
    </AppShell>
  );
}
